import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  CalendarIcon,
  Loader2,
  ChevronRight,
  Droplet,
  Truck,
  Stethoscope,
  Users,
  Ambulance,
  HelpCircle,
  Clock4,
  MapPin,
  Building2,
  User,
  Phone,
  Mail,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

import { Logo } from "@/components/Logo";
import { TrustBar } from "@/components/TrustBar";
import { SuccessPanel } from "@/components/SuccessPanel";
import { ServicePricing } from "@/components/ServicePricing";
import { HowItWorks } from "@/components/HowItWorks";
import { FacilityCallout } from "@/components/ConversionSections";
import { FacilityHowItWorks } from "@/components/FacilityHowItWorks";
import { SiteFooter } from "@/components/SiteFooter";
import { createLead, SERVICES, URGENCY, URGENCY_LABELS, listPublicServices, uploadLabOrder } from "@/lib/api";
import {
  PhlebotomyIntake, CourierIntake, TransportIntake,
  StaffingIntake, ClinicalIntake,
} from "@/components/intake/ServiceIntakeFields";

const ICONS = { Droplet, Truck, Stethoscope, Users, Ambulance, HelpCircle };

// Map service LABEL → intake key used in the service_intake object
const SERVICE_LABELS = {
  phleb: "Phlebotomist Service",
  multi: "Multi-Patient Collection",
  courier: "Medical Courier Service",
  transport: "Non-Emergency Patient Transportation",
  staffing: "Medical Staffing Support",
  clinical: "Clinical Assistant Services",
};

const initialState = {
  full_name: "",
  phone: "",
  email: "",
  company: "",
  services: [],
  service_address: "",
  preferred_date: undefined,
  preferred_time: "",
  urgency: "Standard",
  notes: "",
};

const initialIntake = {
  phleb: {},
  courier: {},
  transport: {},
  staffing: {},
  clinical: {},
};

export default function RequestService() {
  const [form, setForm] = useState(initialState);
  const [intake, setIntake] = useState(initialIntake);
  const [labOrderFile, setLabOrderFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState(null);
  const [errors, setErrors] = useState({});
  const [services, setServices] = useState(
    SERVICES.map((s) => ({ ...s, available: true })),
  );

  const setIntakeKey = (group, key, value) =>
    setIntake((s) => ({ ...s, [group]: { ...s[group], [key]: value } }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listPublicServices();
        if (!cancelled && Array.isArray(data) && data.length > 0) setServices(data);
      } catch {
        // fallback to static SERVICES already in state
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleService = (label) => {
    setForm((s) => {
      const has = s.services.includes(label);
      return { ...s, services: has ? s.services.filter((x) => x !== label) : [...s.services, label] };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (form.services.length === 0) e.services = "Select at least one service.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    try {
      setSubmitting(true);
      // Build service_intake — only include groups whose service is selected
      const intakePayload = {};
      const isSelected = (label) => form.services.includes(label);
      if (isSelected(SERVICE_LABELS.phleb) || isSelected(SERVICE_LABELS.multi)) {
        intakePayload.phlebotomy = intake.phleb;
      }
      if (isSelected(SERVICE_LABELS.courier)) intakePayload.courier = intake.courier;
      if (isSelected(SERVICE_LABELS.transport)) intakePayload.transport = intake.transport;
      if (isSelected(SERVICE_LABELS.staffing)) intakePayload.staffing = intake.staffing;
      if (isSelected(SERVICE_LABELS.clinical)) intakePayload.clinical = intake.clinical;

      const payload = {
        ...form,
        company: form.company || null,
        service_address: form.service_address || null,
        preferred_date: form.preferred_date ? format(form.preferred_date, "yyyy-MM-dd") : null,
        preferred_time: form.preferred_time || null,
        notes: form.notes || null,
        service_intake: Object.keys(intakePayload).length ? intakePayload : null,
      };
      const created = await createLead(payload);
      // If a lab order file is attached + phleb selected, upload it now
      if (labOrderFile && (isSelected(SERVICE_LABELS.phleb) || isSelected(SERVICE_LABELS.multi))) {
        try {
          await uploadLabOrder(created.id, labOrderFile);
        } catch (err) {
          // Don't block the success state — surface a warning instead
          toast.warning("Request received, but lab order upload failed. We'll follow up.");
        }
      }
      setSubmittedRequestId(created.request_id || null);
      setSubmitted(true);
      toast.success(`Request received${created.request_id ? ` — ${created.request_id}` : ""}. We will be in touch shortly.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const onReset = () => {
    setForm(initialState);
    setIntake(initialIntake);
    setLabOrderFile(null);
    setErrors({});
    setSubmitted(false);
    setSubmittedRequestId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const dateLabel = useMemo(
    () => (form.preferred_date ? format(form.preferred_date, "PPP") : "Select a date"),
    [form.preferred_date],
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Top nav */}
      <header
        className="sticky top-0 z-30 border-b bg-white/90 backdrop-blur"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo size="md" showText />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 tj-grid-bg" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-12 sm:py-16">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div
                className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1"
                style={{ borderColor: "#E2E8F0" }}
              >
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} />
                <span
                  className="text-[11px] uppercase tracking-[0.22em] font-medium"
                  style={{ color: "#0B2545" }}
                >
                  For Individuals &amp; Families
                </span>
              </div>
              <h1
                className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
                style={{ color: "#0B2545" }}
                data-testid="page-title"
              >
                Request Service
              </h1>
              <div className="tj-gold-rule mt-5" />
              <p
                className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed"
                style={{ color: "#475569" }}
                data-testid="page-subtitle"
              >
                Professional mobile services delivered to your home, office, or
                preferred location. Complete the form below and our team will
                contact you shortly.
              </p>
            </div>

            <aside className="lg:col-span-5">
              <div
                className="rounded-xl border p-6 h-full"
                style={{ borderColor: "#E2E8F0", background: "#0B2545" }}
                data-testid="hero-info-panel"
              >
                <div
                  className="text-[11px] uppercase tracking-[0.22em] font-medium"
                  style={{ color: "#E6C765" }}
                >
                  Care Is In Motion
                </div>
                <h3
                  className="font-display mt-3 text-2xl font-semibold"
                  style={{ color: "#FFFFFF" }}
                >
                  Concierge-grade clinical operations on demand.
                </h3>
                <ul className="mt-6 space-y-3">
                  {[
                    "Mobile phlebotomy & specimen collection",
                    "STAT medical courier dispatch",
                    "Multi-patient household visits",
                    "Patient transportation, with dignity",
                  ].map((line) => (
                    <li
                      key={line}
                      className="flex items-start gap-3 text-sm"
                      style={{ color: "#FFFFFF" }}
                    >
                      <ChevronRight
                        className="mt-0.5 h-4 w-4 shrink-0"
                        style={{ color: "#D4AF37" }}
                        strokeWidth={2.4}
                      />
                      <span style={{ color: "rgba(255,255,255,0.92)" }}>{line}</span>
                    </li>
                  ))}
                </ul>
                <div
                  className="mt-7 rounded-md border px-4 py-3"
                  style={{ borderColor: "rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.08)" }}
                >
                  <div
                    className="text-[10px] uppercase tracking-[0.22em] font-medium"
                    style={{ color: "#E6C765" }}
                  >
                    Average response
                  </div>
                  <div className="text-sm font-medium" style={{ color: "#FFFFFF" }}>
                    Under 30 minutes during business hours.
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Trust strip — full width, under hero */}
      <TrustBar variant="strip" />

      {/* Service pricing */}
      <ServicePricing />

      {/* How it works */}
      <HowItWorks />

      {/* Form */}
      <section
        id="request-form"
        className="mx-auto max-w-5xl px-5 sm:px-8 py-16 sm:py-20"
        data-testid="form-section"
      >
        {submitted ? (
          <SuccessPanel onReset={onReset} requestId={submittedRequestId} />
        ) : (
          <form
            onSubmit={onSubmit}
            className="rounded-xl border bg-white"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="request-service-form"
            noValidate
          >
            {/* Section: Contact */}
            <FormSection
              index="01"
              title="Contact information"
              description="So our coordinators can reach you quickly."
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" required error={errors.full_name} icon={User}>
                  <Input
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    placeholder="Jane Doe"
                    className="h-11"
                    data-testid="input-full-name"
                  />
                </Field>
                <Field label="Phone number" required error={errors.phone} icon={Phone}>
                  <Input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    inputMode="tel"
                    className="h-11"
                    data-testid="input-phone"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Email" required error={errors.email} icon={Mail}>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@company.com"
                      className="h-11"
                      data-testid="input-email"
                    />
                  </Field>
                </div>
              </div>
            </FormSection>

            <Divider />

            {/* Section: Business */}
            <FormSection
              index="02"
              title="Business information"
              description="Optional — helpful for facilities and partners."
            >
              <Field label="Company / Facility name" icon={Building2}>
                <Input
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  placeholder="Sunrise Medical Center (optional)"
                  className="h-11"
                  data-testid="input-company"
                />
              </Field>
            </FormSection>

            <Divider />

            {/* Section: Services */}
            <FormSection
              index="03"
              title="Service requested"
              description="Select all that apply. You can request multiple services in one submission."
            >
              {errors.services && (
                <p
                  className="mb-3 text-sm font-medium"
                  style={{ color: "#B91C1C" }}
                  data-testid="services-error"
                >
                  {errors.services}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {services.map((s) => {
                  const Icon = ICONS[s.icon] || HelpCircle;
                  const checked = form.services.includes(s.label);
                  const disabled = s.available === false;
                  return (
                    <label
                      key={s.id}
                      className={`group flex items-start gap-3 rounded-lg border p-4 transition-all duration-200 ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:-translate-y-[1px]"}`}
                      style={{
                        borderColor: disabled ? "#E2E8F0" : (checked ? "#0B2545" : "#E2E8F0"),
                        background: disabled ? "#F8FAFC" : (checked ? "rgba(11, 37, 69, 0.03)" : "#FFFFFF"),
                        boxShadow: !disabled && checked ? "inset 0 0 0 1px #0B2545" : "none",
                        opacity: disabled ? 0.6 : 1,
                      }}
                      data-testid={`service-card-${s.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={() => !disabled && toggleService(s.label)}
                        className="mt-0.5"
                        data-testid={`service-checkbox-${s.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon
                            className="h-4 w-4"
                            style={{ color: disabled ? "#94A3B8" : (checked ? "#D4AF37" : "#0B2545") }}
                            strokeWidth={2.2}
                          />
                          <span
                            className="text-sm font-semibold"
                            style={{ color: disabled ? "#64748B" : "#0B2545" }}
                          >
                            {s.label}
                          </span>
                          {disabled && (
                            <span
                              className="text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
                              style={{ background: "rgba(155,28,28,0.10)", color: "#9B1C1C" }}
                              data-testid={`service-unavailable-${s.id}`}
                            >
                              Temporarily unavailable
                            </span>
                          )}
                        </div>
                        <p
                          className="mt-1 text-xs leading-relaxed"
                          style={{ color: "#475569" }}
                        >
                          {s.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Conditional service-specific intake sections */}
              {(form.services.includes(SERVICE_LABELS.phleb) || form.services.includes(SERVICE_LABELS.multi)) && (
                <div className="mt-5">
                  <PhlebotomyIntake
                    data={intake.phleb}
                    setData={(k, v) => setIntakeKey("phleb", k, v)}
                    file={labOrderFile}
                    setFile={setLabOrderFile}
                  />
                </div>
              )}
              {form.services.includes(SERVICE_LABELS.courier) && (
                <div className="mt-5">
                  <CourierIntake
                    data={intake.courier}
                    setData={(k, v) => setIntakeKey("courier", k, v)}
                  />
                </div>
              )}
              {form.services.includes(SERVICE_LABELS.transport) && (
                <div className="mt-5">
                  <TransportIntake
                    data={intake.transport}
                    setData={(k, v) => setIntakeKey("transport", k, v)}
                  />
                </div>
              )}
              {form.services.includes(SERVICE_LABELS.staffing) && (
                <div className="mt-5">
                  <StaffingIntake
                    data={intake.staffing}
                    setData={(k, v) => setIntakeKey("staffing", k, v)}
                  />
                </div>
              )}
              {form.services.includes(SERVICE_LABELS.clinical) && (
                <div className="mt-5">
                  <ClinicalIntake
                    data={intake.clinical}
                    setData={(k, v) => setIntakeKey("clinical", k, v)}
                  />
                </div>
              )}
            </FormSection>

            <Divider />

            {/* Section: Details */}
            <FormSection
              index="04"
              title="Service details"
              description="Tell us where, when, and how urgent."
            >
              <div className="grid gap-5">
                <Field label="Service address" icon={MapPin}>
                  <Input
                    value={form.service_address}
                    onChange={(e) => set("service_address", e.target.value)}
                    placeholder="Street, city, state, ZIP"
                    className="h-11"
                    data-testid="input-address"
                  />
                </Field>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Preferred date" icon={CalendarIcon}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 w-full justify-start font-normal"
                          style={{ borderColor: "#E2E8F0", color: form.preferred_date ? "#0F172A" : "#94A3B8" }}
                          data-testid="trigger-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateLabel}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.preferred_date}
                          onSelect={(d) => set("preferred_date", d)}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>

                  <Field label="Preferred time" icon={Clock4}>
                    <Input
                      type="time"
                      value={form.preferred_time}
                      onChange={(e) => set("preferred_time", e.target.value)}
                      className="h-11"
                      data-testid="input-time"
                    />
                  </Field>
                </div>

                <div
                  className="rounded-lg border p-4"
                  style={{ borderColor: "rgba(155,28,28,0.30)", background: "rgba(155,28,28,0.04)" }}
                  data-testid="urgent-notice"
                >
                  <div className="flex items-start gap-2">
                    <span className="grid place-items-center h-7 w-7 rounded-md shrink-0"
                          style={{ background: "rgba(155,28,28,0.10)", color: "#9B1C1C" }}>
                      <Clock4 className="h-3.5 w-3.5" />
                    </span>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#9B1C1C" }}>
                        Urgent / Same-Day Service Available
                      </div>
                      <p className="mt-1 text-xs leading-relaxed" style={{ color: "#475569" }}>
                        For urgent phlebotomy, medical courier, transportation, staffing, or
                        clinical support requests, select <strong>Urgent (Same Day)</strong> or
                        <strong> STAT / Immediate Review</strong> and provide details in the notes
                        section. Our team will prioritize review and respond as quickly as possible
                        based on availability.
                      </p>
                    </div>
                  </div>
                </div>

                <Field label="Urgency level">
                  <Select
                    value={form.urgency}
                    onValueChange={(v) => set("urgency", v)}
                  >
                    <SelectTrigger
                      className="h-11"
                      style={{ borderColor: "#E2E8F0" }}
                      data-testid="trigger-urgency"
                    >
                      <SelectValue placeholder="Select urgency" />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY.map((u) => (
                        <SelectItem key={u} value={u} data-testid={`urgency-option-${u.toLowerCase().replace(/\s+/g, "-")}`}>
                          {URGENCY_LABELS[u] || u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Notes / special instructions">
                  <Textarea
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Anything we should know? Access codes, contact on-site, special requirements..."
                    rows={5}
                    data-testid="input-notes"
                  />
                </Field>
              </div>
            </FormSection>

            {/* Submit */}
            <div
              className="rounded-b-xl border-t px-6 sm:px-10 py-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:justify-between"
              style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
            >
              <p className="text-xs sm:text-sm" style={{ color: "#475569" }}>
                By submitting, you agree to be contacted by T&amp;J Clinical
                Solutions about your request.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="h-14 px-8 text-base sm:text-lg font-semibold tracking-wide text-white shadow-sm transition-all duration-200 hover:-translate-y-[1px]"
                style={{ background: "#0B2545" }}
                data-testid="submit-request-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>Request Service Now</>
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Payment note */}
        <p
          className="mt-5 text-center text-sm"
          style={{ color: "#475569" }}
          data-testid="payment-note"
        >
          <span
            className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5"
            style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
          >
            <Lock className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} />
            Payment is arranged after appointment confirmation.
          </span>
        </p>
      </section>

      {/* Returning client section temporarily hidden — focus is on new bookings for now.
          Re-enable later by uncommenting `<ReturningClient />` and re-adding the import. */}

      {/* Facility / Provider partnership process */}
      <FacilityHowItWorks />

      {/* Facility / Provider callout */}
      <FacilityCallout />

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}

const FormSection = ({ index, title, description, children }) => (
  <div className="px-6 sm:px-10 pt-8 sm:pt-10">
    <div className="flex items-baseline gap-4">
      <span
        className="font-display text-xs font-medium tracking-[0.22em]"
        style={{ color: "#D4AF37" }}
      >
        {index}
      </span>
      <div>
        <h3
          className="font-display text-xl sm:text-2xl font-semibold"
          style={{ color: "#0B2545" }}
        >
          {title}
        </h3>
        <p className="mt-1 text-sm" style={{ color: "#475569" }}>
          {description}
        </p>
      </div>
    </div>
    <div className="mt-6 pb-2">{children}</div>
  </div>
);

const Divider = () => (
  <div
    className="mx-6 sm:mx-10 mt-8 border-t"
    style={{ borderColor: "#E2E8F0" }}
  />
);

const Field = ({ label, required, error, icon: Icon, children }) => (
  <div>
    <Label
      className="mb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium"
      style={{ color: "#0B2545" }}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" style={{ color: "#D4AF37" }} /> : null}
      {label}
      {required ? <span style={{ color: "#D4AF37" }}>*</span> : null}
    </Label>
    {children}
    {error ? (
      <p className="mt-1.5 text-xs font-medium" style={{ color: "#B91C1C" }}>
        {error}
      </p>
    ) : null}
  </div>
);
